import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, ArrowUpRightIcon, CheckIcon } from 'lucide-react';
import { solutions, type SolutionKey } from '../../data/solutions';
import { SectionIntro } from '../layout/SectionIntro';
const accent = {
  orange: {
    text: 'text-forge-orange',
    bg: 'bg-forge-orange',
    soft: 'bg-forge-orange/10',
    border: 'border-forge-orange/35'
  },
  green: {
    text: 'text-forge-green',
    bg: 'bg-forge-green',
    soft: 'bg-forge-green/10',
    border: 'border-forge-green/35'
  },
  cyan: {
    text: 'text-forge-cyan',
    bg: 'bg-forge-cyan',
    soft: 'bg-forge-cyan/10',
    border: 'border-forge-cyan/35'
  }
} as const;
export function Solutions() {
  const [active, setActive] = useState<SolutionKey>('design');
  const current = solutions.find(s => s.key === active) ?? solutions[0];
  const tone = accent[current.accent];
  return <section id="solutions" className="relative overflow-hidden bg-ink py-16 lg:py-24" data-id="element-272" data-mp="3hgiugkzj64z5">
      <div className="absolute inset-0 bp opacity-70" aria-hidden="true" data-id="element-273" data-mp="3i6txhiu33nhe" />
      <div className="absolute right-0 top-10 h-[32rem] w-[32rem] rounded-full bg-navy-700/25 blur-[140px]" aria-hidden="true" data-id="element-274" data-mp="3n5x3b5hw1xqf" />

      <div className="relative mx-auto max-w-8xl px-5 sm:px-8 lg:px-10" data-id="element-275" data-mp="38g91m35sox26">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between" data-id="element-276" data-mp="3dg3sw7thfonr">
          <SectionIntro eyebrow="Our solutions" title={<>
                Six places business websites break.
                <span className="block text-mist-300" data-id="element-278" data-mp="34h9xulvsv6cb">We handle every one of them.</span>
              </>} body="Pick the problem you recognise. Every engagement starts with something you can name — not a package you have to decode." data-id="element-277" data-mp="2zgm1dkosxxap" />
          <Link to="/#get-started" className="group inline-flex shrink-0 items-center gap-2 self-start rounded-xl border border-white/15 px-5 py-3 text-[14px] font-semibold text-white transition-colors hover:border-forge-orange/60 hover:bg-white/5" data-id="element-279" data-mp="nqtdxh1vhogr">
            Tell us what&apos;s not working
            <ArrowUpRightIcon className="h-4 w-4 text-forge-orange" aria-hidden="true" data-id="element-280" data-mp="m8b8nfxv25zt" />
          </Link>
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-12 lg:gap-8" data-id="element-281" data-mp="hcvwcsfi4i5i">
          {/* index rail */}
          <div className="lg:col-span-4" data-id="element-282" data-mp="dbaojlhtqu26">
            <ul className="flex gap-2 overflow-x-auto pb-2 lg:flex-col lg:gap-1.5 lg:overflow-visible lg:pb-0" data-id="element-283" data-mp="1a004v7dszem1">
              {solutions.map((s, i) => {
              const isActive = s.key === active;
              const t = accent[s.accent];
              return <li key={s.key} className="shrink-0 lg:shrink" data-id="element-284" data-mp="w824bky5qr1h" data-mpi={i}>
                    <button type="button" onClick={() => setActive(s.key)} aria-pressed={isActive} className={`group flex w-full items-center gap-3 rounded-xl border px-4 py-3.5 text-left transition-colors ${isActive ? `${t.border} bg-white/[0.06]` : 'border-white/8 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]'}`} data-id="element-285" data-mp="w7tp3mekb9n4">
                      <span className={`font-mono text-[10px] tracking-[0.14em] ${isActive ? t.text : 'text-mist-400'}`} data-id="element-286" data-mp="3klixqpp70alw">
                        0{i + 1}
                      </span>
                      <span className={`whitespace-nowrap text-[13.5px] font-semibold lg:whitespace-normal ${isActive ? 'text-white' : 'text-mist-200'}`} data-id="element-287" data-mp="3uix2hs1r7ayd">
                        {s.label}
                      </span>
                      <ArrowRightIcon className={`ml-auto hidden h-4 w-4 shrink-0 transition-transform lg:block ${isActive ? `${t.text} translate-x-0` : 'text-white/20 -translate-x-1'}`} aria-hidden="true" data-id="element-288" data-mp="c7ipful4hrey" />
                    </button>
                  </li>;
            })}
            </ul>
          </div>

          {/* detail panel */}
          <div className="lg:col-span-8" data-id="element-289" data-mp="8a35xjhfiuae">
            <AnimatePresence mode="wait" data-id="element-290" data-mp="8b4sol7t557y">
              <motion.article key={current.key} initial={{
              opacity: 0,
              y: 14
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.28
            }} className="relative h-full overflow-hidden rounded-2xl border border-white/10 bg-navy-950/80 p-6 sm:p-8" data-id="element-291" data-mp="3gobv0hkqt2xt">
                <div className="absolute inset-0 bp-fine opacity-60" aria-hidden="true" data-id="element-292" data-mp="15wk4st5t9fy9" />
                <span className={`absolute inset-x-0 top-0 h-[3px] ${tone.bg}`} aria-hidden="true" data-id="element-293" data-mp="10xzcdfwsw0kd" />

                <div className="relative grid gap-8 lg:grid-cols-5" data-id="element-294" data-mp="18hi1m3wn0q1e">
                  <div className="lg:col-span-3" data-id="element-295" data-mp="18jbbds2jrqdm">
                    <p className={`text-[12px] font-semibold uppercase tracking-[0.12em] ${tone.text}`} data-id="element-296" data-mp="1dgl5hl0vsq7s">
                      {current.problem}
                    </p>
                    <h3 className="mt-3 font-display text-[1.4rem] font-extrabold leading-tight text-white sm:text-[1.6rem]" data-id="element-297" data-mp="13i5hvs12bz31">
                      {current.headline}
                    </h3>
                    <p className="mt-4 text-[14.5px] leading-relaxed text-mist-200" data-id="element-298" data-mp="1ra2hmkr8n0ns">{current.body}</p>

                    <Link to="/#get-started" className={`group mt-7 inline-flex items-center gap-2 rounded-xl border ${tone.border} ${tone.soft} px-5 py-3 text-[14px] font-bold text-white transition-colors hover:bg-white/10`} data-id="element-299" data-mp="1ifebksdu9exx">
                      Talk through this
                      <ArrowRightIcon className={`h-4 w-4 ${tone.text} transition-transform group-hover:translate-x-1`} aria-hidden="true" data-id="element-300" data-mp="2exk9egai5yqr" />
                    </Link>
                  </div>

                  <div className="lg:col-span-2" data-id="element-301" data-mp="2kr5wmd5ui543">
                    <p className="font-mono text-[9.5px] uppercase tracking-[0.2em] text-mist-400" data-id="element-302" data-mp="2kpl4svax109x">What we deliver</p>
                    <ul className="mt-4 space-y-3" data-id="element-303" data-mp="2ati3tsrsv7ok">
                      {current.deliverables.map(d => <li key={d} className="flex gap-2.5 text-[13.5px] leading-snug text-mist-100" data-id="element-304" data-mp="25u6kqd3ynq3t">
                          <span className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full ${tone.soft}`} data-id="element-305" data-mp="213yh2c0yutof">
                            <CheckIcon className={`h-3 w-3 ${tone.text}`} aria-hidden="true" data-id="element-306" data-mp="1x369robnmyus" />
                          </span>
                          {d}
                        </li>)}
                    </ul>
                  </div>
                </div>
              </motion.article>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>;
}